import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { searchUsers } from '../../redux/slices/userSlice';
import { Search, User } from 'lucide-react';

/**
 * UserSearchAutocomplete
 * 
 * Reusable component to search for users by name/email.
 * 
 * @param {Function} onSelect - Callback when a user is selected (user object)
 * @param {Array} excludeIds - List of user IDs to exclude from results (e.g. already selected)
 * @param {String} placeholder - Input placeholder
 * @param {Function} onNoUsersFound - Callback when no users found (passes email/query)
 */
const UserSearchAutocomplete = ({ onSelect, excludeIds = [], placeholder = "Search users by name or email...", onNoUsersFound }) => {
    const dispatch = useDispatch();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (query.trim().length >= 2) {
                setLoading(true);
                try {
                    const resultAction = await dispatch(searchUsers(query));
                    if (searchUsers.fulfilled.match(resultAction)) {
                        const allUsers = resultAction.payload;
                        // Filter out excluded IDs
                        const filtered = allUsers.filter(u => !excludeIds.includes(u.id));
                        setResults(filtered);
                        setIsOpen(true);
                    } else {
                    }
                } catch (err) {
                } finally {
                    setLoading(false);
                }
            } else {
                setResults([]);
                setIsOpen(false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [query, dispatch, excludeIds]); // Added excludeIds dependency to re-filter if selection changes while searching

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const handleSelect = (user) => {
        onSelect(user);
        setQuery('');
        setResults([]);
        setIsOpen(false);
    };

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => {
                        if (query.length >= 2 && results.length > 0) setIsOpen(true);
                    }}
                />
                {loading && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                )}
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {results.length > 0 ? (
                        results.map((user) => (
                            <div
                                key={user.id}
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                                onClick={() => handleSelect(user)}
                            >
                                <div className="flex items-center">
                                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs mr-3">
                                        {user.full_name ? user.full_name.charAt(0).toUpperCase() : <User size={14} />}
                                    </div>
                                    <div>
                                        <span className="block truncate font-medium text-gray-900">
                                            {user.full_name}
                                        </span>
                                        <span className="block truncate text-xs text-gray-500">
                                            {user.email}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-2 px-3 space-y-2">
                            <div className="text-gray-700 italic text-sm">
                                No users found.
                            </div>
                            {query.trim().length >= 2 && onNoUsersFound && (
                                <button
                                    onClick={() => onNoUsersFound(query)}
                                    className="w-full px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <span>+</span>
                                    Add User "{query}"
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserSearchAutocomplete;
